import { GlobalStateController } from "bai-react-global-state";
import { v4 as uuid } from "uuid";

import {
    AiGeneratedSlideGroupType,
    AiGeneratedSlideType,
    AiGeneratedSlideTypeGroups,
    GeneratePresentationOutlineResponse,
    GeneratedSlideModel,
    ImageStyles
} from "common/aiConstants";
import { MetricName } from "common/interfaces";
import { getDefaultTheme } from "common/themes";
import { withMaxConcurrency } from "common/utils/withMaxConcurrency";
import WorkspaceController from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { Theme } from "js/core/models/theme";
import AiGenerationService, { GeneratePresentationOutlineRequestWithFiles, InsufficientCreditsError, TooManyRequestsError, UnprocessableContextError } from "js/core/services/aiGeneration";
import * as geom from "js/core/utilities/geom";
import { ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { ShowInsufficientCreditsDialog, ShowTooManyRequestsDialog } from "js/react/components/Dialogs/CreditsErrorDialog";

const logger = getLogger(LogGroup.AI);

export enum PresentationGenerationStep {
    CHOOSE_FLOW = "choose-flow",
    PROMPT = "prompt",
    DOC2DECK = "doc-to-deck",
    GENERATING_OUTLINE = "generating-outline",
    EDITING_OUTLINE = "editing-outline",
    GENERATING_SLIDES = "generating-slides",
    THEME = "theme",
    DONE = "done"
}

export interface StartGenerationParams {
    prompt: string;
    imageStyle?: ImageStyles;
    imageStylePrompt?: string;
    files?: File[];
    contextUrls?: string[];
    documentText?: string;
    language?: string;
}

export interface SlideOutline {
    id: string;
    title: string;
    summary: string;
    prompt?: string;
    presentationPrompt?: string;
    imageStylePrompt?: string;
    type: AiGeneratedSlideType | "";

    bounds?: geom.Rect;
    dragBounds?: geom.Rect;
}

interface FauxBackboneTheme {
    id: string;
    get: (key: string) => any;
    loadTheme: () => Promise<void>;
}

export interface GeneratePresentationState {
    step: PresentationGenerationStep;
    startGenerationParams?: StartGenerationParams;
    theme: FauxBackboneTheme;
    generatePresentationOutlineResponse?: GeneratePresentationOutlineResponse;
    slideOutlines: SlideOutline[];
    slideModels: GeneratedSlideModel[];
    showChooseThemeDialog: boolean;
    aiCreditsBalance: number;
    aiGenImagesEnabled: boolean;
    workspaceId: string | null;
}

const initialState: GeneratePresentationState = {
    step: PresentationGenerationStep.PROMPT,
    theme: new Theme(getDefaultTheme(), { disconnected: true, autoLoad: false }),
    slideOutlines: [],
    slideModels: [],
    startGenerationParams: undefined,
    generatePresentationOutlineResponse: undefined,
    showChooseThemeDialog: false,
    aiCreditsBalance: 0,
    aiGenImagesEnabled: false,
    workspaceId: null
};

export interface OnPresentationGeneratedCallbackParams {
    type: string,
    defaultSlides: Object[],
    theme: Object,
    name: string,
    imageStyle: ImageStyles,
    imageStylePrompt: string,
    language: string,
    prompt: string,
    metadata: GeneratePresentationOutlineResponse & { imageStyle: ImageStyles, imageStylePrompt: string }
}

class GeneratePresentationController extends GlobalStateController<GeneratePresentationState> {
    onPresentataionGeneratedCallback?: (params: OnPresentationGeneratedCallbackParams) => void

    async reset(
        workspaceId: string,
        onPresentataionGeneratedCallback?: (params: OnPresentationGeneratedCallbackParams) => void
    ) {
        const state = { ...initialState, workspaceId };

        const teamThemes = await WorkspaceController.getSharedThemes(true, true);
        const userThemes = await WorkspaceController.getUserThemes(true);

        if (teamThemes?.length > 0) {
            state.theme = teamThemes[0];
        } else if (userThemes?.length > 0) {
            state.theme = userThemes[0];
        } else {
            state.theme = new Theme(getDefaultTheme(), { disconnected: true, autoLoad: false });
        }

        const aiGenImagesEnabled = WorkspaceController.experiments.ai_gen_images;
        state.aiGenImagesEnabled = aiGenImagesEnabled;

        await this._updateState(() => state);

        await this.loadAiCreditsBalance();

        if (onPresentataionGeneratedCallback) {
            this.onPresentataionGeneratedCallback = onPresentataionGeneratedCallback;
        }
    }

    async showChooseFlow() {
        await this._updateState({
            step: PresentationGenerationStep.CHOOSE_FLOW
        });
    }

    async showPrompt() {
        await this._updateState({
            step: PresentationGenerationStep.PROMPT
        });
    }

    async showDocToDeck() {
        await this._updateState({
            step: PresentationGenerationStep.DOC2DECK
        });
    }

    async showGeneratingOutline() {
        await this._updateState({
            step: PresentationGenerationStep.GENERATING_OUTLINE
        });
    }

    async showEditingOutline() {
        await this._updateState({
            step: PresentationGenerationStep.EDITING_OUTLINE
        });
    }

    async showTheme() {
        await this._updateState({
            step: PresentationGenerationStep.THEME
        });
    }

    getStartGenerationParams() {
        return this._state.startGenerationParams;
    }

    async updateStartGenerationParams(startGenerationParams: StartGenerationParams) {
        await this._updateState({ startGenerationParams: { ...this._state.startGenerationParams, ...startGenerationParams } });
    }

    async generatePresentationOutline(startGenerationParams: StartGenerationParams) {
        await this._updateState({
            step: PresentationGenerationStep.GENERATING_OUTLINE,
            startGenerationParams
        });

        const startedAt = Date.now();
        const withContext = startGenerationParams.contextUrls?.length > 0 || startGenerationParams.files?.length > 0 || !!startGenerationParams.documentText;

        try {
            await this._generatePresentationOutline();
            await this._generateSlideOutlines();
            await this.loadAiCreditsBalance();
            await this.generatePresentationFromOutline();

            logger.metric(MetricName.AI_GENERATE_PRESENTATION_TIME, {
                generationTimeMs: Date.now() - startedAt,
                slideCount: this._state.slideModels.filter(Boolean).length,
                withContext,
                generationFailed: false
            });
        } catch (err) {
            logger.error(err, "[GeneratePresentationController] error generating presentation", { startGenerationParams });

            if (err instanceof InsufficientCreditsError) {
                ShowInsufficientCreditsDialog(() => this.loadAiCreditsBalance(true));
            } else if (err instanceof TooManyRequestsError) {
                ShowTooManyRequestsDialog();
            } else if (err instanceof UnprocessableContextError) {
                ShowErrorDialog({
                    title: "Sorry, we do not support the file type you have provided.",
                    message: "Please try again with a text file, word document, or PDF."
                });
            } else {
                ShowErrorDialog({
                    title: "Oops, DesignerBot AI wasn't able to generate a valid presentation for your prompt.",
                    message: "This can happen unexpectedly sometimes. You can either try again or try to rephrase your prompt."
                });
            }

            logger.metric(MetricName.AI_GENERATE_PRESENTATION_TIME, {
                generationTimeMs: Date.now() - startedAt,
                slideCount: this._state.slideModels.filter(Boolean).length,
                withContext,
                generationFailed: true
            });

            await this._updateState({ step: PresentationGenerationStep.PROMPT });
        }
    }

    async setSlideOutlines(slideOutlines: SlideOutline[]) {
        await this._updateState({ slideOutlines });
    }

    async setShowChooseThemeDialog(showChooseThemeDialog: boolean) {
        await this._updateState({ showChooseThemeDialog });
    }

    async setTheme(theme: FauxBackboneTheme) {
        await this._updateState({ theme });
    }

    async generatePresentationFromOutline() {
        try {
            await this._updateState({ step: PresentationGenerationStep.GENERATING_SLIDES, slideModels: new Array(this._state.slideOutlines.length).fill(null) });

            await this._state.theme.loadTheme();

            await withMaxConcurrency(this._state.slideOutlines.map((slideOutline, idx) => async () => {
                const model = await this._generateSlideModel(slideOutline, idx);
                const slideModels = [...this._state.slideModels];
                slideModels[idx] = model;
                await this._updateState({ slideModels });
            }), 5);

            const defaultSlides = this._state.slideModels
                .filter(Boolean);

            if (defaultSlides.length === 0) {
                throw new Error("Couldn't generate any slides");
            }

            await this.loadAiCreditsBalance();

            if (this.onPresentataionGeneratedCallback) {
                this.onPresentataionGeneratedCallback({
                    type: "generate-with-ai",
                    defaultSlides,
                    theme: this._state.theme,
                    name: this._state.generatePresentationOutlineResponse.title.replace(/\*/g, ""),
                    imageStyle: this._state.startGenerationParams?.imageStyle,
                    imageStylePrompt: this._state.startGenerationParams?.imageStylePrompt,
                    language: this._state.startGenerationParams?.language,
                    prompt: this._state.startGenerationParams?.prompt,
                    metadata: {
                        ...this._state.generatePresentationOutlineResponse,
                        imageStyle: this._state.startGenerationParams?.imageStyle,
                        imageStylePrompt: this._state.startGenerationParams?.imageStylePrompt
                    }
                });
            }

            await this._updateState({ step: PresentationGenerationStep.DONE });
        } catch (err) {
            logger.error(err, "[GeneratePresentationController] error generating presentation from outline");

            if (err instanceof InsufficientCreditsError) {
                ShowInsufficientCreditsDialog(() => this.loadAiCreditsBalance(true));
            } else if (err instanceof TooManyRequestsError) {
                ShowTooManyRequestsDialog();
            } else {
                ShowErrorDialog({
                    title: "Oops, DesignerBot AI wasn't able to generate a valid presentation for your outline.",
                    message: "This can happen unexpectedly sometimes. You can either try again or try to rephrase your prompt."
                });
            }

            await this._updateState({ step: PresentationGenerationStep.PROMPT });
        }
    }

    async loadAiCreditsBalance(handleErrors = false) {
        try {
            const { workspaceId } = this._state;
            if (workspaceId === null) {
                await this._updateState({ aiCreditsBalance: 0 });
                return;
            }

            const aiCreditsBalance = await AiGenerationService.getCreditsBalance(workspaceId);
            await this._updateState({ aiCreditsBalance });
        } catch (err) {
            if (!handleErrors) {
                throw err;
            }

            logger.error(err, "[GeneratePresentationController] error loading AI credits balance");
        }
    }

    private async _generatePresentationOutline() {
        const { startGenerationParams } = this._state;
        if (!startGenerationParams) {
            throw new Error("Start generation params are not set");
        }

        const { prompt, files = [], contextUrls = [], documentText = "", language = "" } = startGenerationParams;

        const generatePresentationOutlineRequest: GeneratePresentationOutlineRequestWithFiles = {
            allowedSlideTypes: AiGeneratedSlideTypeGroups[AiGeneratedSlideGroupType.PRESENTATION],
            prompt,
            files: [...files],
            contextUrls,
            language
        };

        if (documentText) {
            generatePresentationOutlineRequest.files.push(new File([documentText], "document.txt", { type: "text/plain" }));
        }

        const generatePresentationOutlineResponse = await AiGenerationService.generatePresentationOutline(generatePresentationOutlineRequest);
        await this._updateState({ generatePresentationOutlineResponse });
    }

    private async _generateSlideOutlines() {
        const { generatePresentationOutlineResponse, startGenerationParams } = this._state;
        const { prompt } = startGenerationParams;

        const slideOutlines: SlideOutline[] = [
            // Predefined title slide
            {
                id: uuid(),
                title: generatePresentationOutlineResponse.title,
                summary: "Introductory slide",
                presentationPrompt: prompt,
                type: AiGeneratedSlideType.TITLE
            },
            // Ai generated slides
            ...generatePresentationOutlineResponse.slides.map(slide => ({
                id: uuid(),
                title: slide.title,
                summary: slide.summary,
                presentationPrompt: prompt,
                type: slide.type,
            })),
            // Conclusion slide (taking in all slide outlines as context)
            {
                id: uuid(),
                presentationPrompt: prompt,
                  title: generatePresentationOutlineResponse.conclusion,
                summary: "Conclusion Slide",
                type: AiGeneratedSlideType.CONCLUSION
            }
        ];

        await this._updateState({ slideOutlines });
    }

    private async _generateSlideModel(slideOutline: SlideOutline, idx: number, isRetry = false) {
        const { generatePresentationOutlineResponse, startGenerationParams, slideOutlines } = this._state;

        try {
            const model = await AiGenerationService.generateSlide({
                prompt: slideOutline.prompt,
                presentationPrompt: slideOutline.presentationPrompt,
                outline: slideOutlines.map(slide => ({ title: slide.title, summary: slide.summary, presentationPrompt: slide.presentationPrompt })),
                slideTitle: slideOutline.title,
                // Empty string is auto
                allowedSlideTypes: slideOutline.type === "" ? Object.values(AiGeneratedSlideType) : [slideOutline.type],
                slideSummary: slideOutline.summary,
                indexFileName: generatePresentationOutlineResponse.indexFileName,
                language: startGenerationParams.language,
                traceData: {
                    parentRequest: generatePresentationOutlineResponse.traceData?.request
                },
                imageStyle: startGenerationParams?.imageStyle,
                imageStylePrompt: startGenerationParams?.imageStylePrompt
            });

            return model;
        } catch (err) {
            if (err instanceof InsufficientCreditsError || err instanceof TooManyRequestsError) {
                throw err;
            }

            if (!isRetry) {
                return this._generateSlideModel(slideOutline, idx, true);
            }

            return null;
        }
    }
}

const generatePresentationController = new GeneratePresentationController(initialState);
export default generatePresentationController;
